import { useRef, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Slider as BaseSlider } from '@base-ui/react/slider'
import { twMerge } from 'tailwind-merge'
import { planAccentClasses, type SelectableV2Plan } from './plan-select-cards'

const STEP_DOLLARS = 1

/**
 * The dialog's top-up picker. Three ways to set the same whole-dollar amount:
 * drag the slider, step it an arrow at a time, or type into the field. The
 * caller owns the dollars to units conversion and passes the resulting label
 * ("for 10 hours", "for 1,000 credits").
 */
export function TopUpAmountPicker({
  plan,
  dollars,
  onDollarsChange,
  minDollars,
  maxDollars,
  amountLabel,
}: {
  plan: SelectableV2Plan
  dollars: number
  onDollarsChange: (dollars: number) => void
  minDollars: number
  maxDollars: number
  amountLabel: string
}) {
  const clampDollars = (value: number) => Math.min(maxDollars, Math.max(minDollars, Math.round(value)))

  return (
    <div className="flex flex-col gap-1 pb-2">
      <BaseSlider.Root
        value={dollars}
        onValueChange={(value) => {
          if (value !== null) onDollarsChange(clampDollars(value))
        }}
        min={minDollars}
        max={maxDollars}
        step={STEP_DOLLARS}
        className="relative w-full"
      >
        <BaseSlider.Control className="flex w-full touch-none select-none items-center py-2">
          <BaseSlider.Track className="bg-foreground/30 h-1.5 w-full select-none rounded-full shadow-sm">
            <BaseSlider.Indicator className={twMerge('select-none rounded-full', planAccentClasses(plan))} />
            {/* The thumb's box is the 16px grab target; the dot inside is what
                you see, so the handle stays small without being fiddly. */}
            <BaseSlider.Thumb
              aria-label="Top-up amount"
              className="group flex size-4 origin-center cursor-pointer select-none items-center justify-center rounded-full outline-none"
            >
              <span className={twMerge('size-3 rounded-full shadow-sm', planAccentClasses(plan))} />
            </BaseSlider.Thumb>
          </BaseSlider.Track>
        </BaseSlider.Control>
      </BaseSlider.Root>

      <div className="flex items-end gap-1.5">
        <AmountField dollars={dollars} onCommit={(value) => onDollarsChange(clampDollars(value))} />
        <div className="flex flex-col self-center">
          <StepperButton
            label="Increase amount"
            disabled={dollars >= maxDollars}
            onClick={() => onDollarsChange(clampDollars(dollars + STEP_DOLLARS))}
            className="items-end"
          >
            <ChevronUp className="size-2.5" strokeWidth={2} />
          </StepperButton>
          <StepperButton
            label="Decrease amount"
            disabled={dollars <= minDollars}
            onClick={() => onDollarsChange(clampDollars(dollars - STEP_DOLLARS))}
            className="items-start"
          >
            <ChevronDown className="size-2.5" strokeWidth={2} />
          </StepperButton>
        </div>
        <span className="text-foreground-placeholder pb-0.5 text-[13px] font-medium">{amountLabel}</span>
      </div>
    </div>
  )
}

/**
 * One arrow of the amount stepper. The glyph is small to match the design, so
 * the button pads out to a usable target and pins the arrow to its inner edge,
 * keeping the pair tight instead of drifting apart.
 */
function StepperButton({
  label,
  disabled,
  onClick,
  className,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  /** Pins the arrow to the edge it shares with its sibling. */
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={twMerge(
        'text-foreground-placeholder hover:text-foreground flex h-3 w-4 justify-center bg-transparent p-0 transition-colors disabled:opacity-30',
        className,
      )}
    >
      {children}
    </button>
  )
}

/**
 * The editable dollar amount. Typing is held in a draft string so a
 * half-finished number (or an empty field) survives keystrokes, and the value
 * is only clamped and pushed up on blur or Enter.
 */
function AmountField({ dollars, onCommit }: { dollars: number; onCommit: (dollars: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null)
  // Mirrored in a ref because blur() is synchronous: Escape and Enter both
  // clear the draft and then blur, and the onBlur handler that fires is the one
  // from the render before the state update. Reading state there would make
  // Escape commit the value it was meant to discard, and Enter commit twice.
  const draftRef = useRef<string | null>(null)
  const value = draft ?? String(dollars)

  const setDraftValue = (next: string | null) => {
    draftRef.current = next
    setDraft(next)
  }

  const commit = () => {
    const pending = draftRef.current
    if (pending === null) {
      return
    }
    const parsed = Number.parseInt(pending, 10)
    setDraftValue(null)
    onCommit(Number.isNaN(parsed) ? dollars : parsed)
  }

  return (
    <div className="border-foreground/70 text-foreground flex items-baseline border-b text-[24px] font-medium leading-6">
      <span aria-hidden="true">$</span>
      <input
        value={value}
        aria-label="Top-up amount in dollars"
        inputMode="numeric"
        onChange={(event) => setDraftValue(event.target.value.replace(/\D/g, ''))}
        onFocus={(event) => event.target.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commit()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            // The dialog also closes on Escape. While a draft is being typed,
            // Escape means "discard what I typed", not "abandon the purchase",
            // so it stops here and only falls through once the field is clean.
            if (draftRef.current !== null) {
              event.stopPropagation()
            }
            setDraftValue(null)
            event.currentTarget.blur()
          }
        }}
        // Sized to the digits so the underline hugs the number the way the
        // design draws it; tabular figures keep the ch unit honest.
        style={{ width: `${Math.max(value.length, 1)}ch` }}
        // Font and color are set here rather than inherited: the UA stylesheet
        // wins over the wrapper for input text.
        className="text-foreground bg-transparent p-0 text-[24px] font-medium tabular-nums leading-6 outline-none"
      />
    </div>
  )
}
